/**
 * What's My Size - After Five Studio
 *
 * SINGLE SOURCE OF TRUTH: Google Sheet → whats-my-size-data.js
 *   - window.__AF_SIZE_CHARTS  = brand size charts (measurements)
 *   - window.__AF_PRODUCTS     = per-product info (brand + size & fit text)
 *
 * The calculator lives inside the product page's "Size help" dialog: sections/main-product.liquid
 * renders an empty <section data-wms-mount data-wms-handle="…" data-wms-available-sizes="S,M,L">
 * and this script builds the form into it (styles: assets/af-pdp.css, .af-wms). When the product
 * has no chart data the mount stays hidden and the dialog shows the size chart only.
 * The tool looks up the product handle → gets brand → gets that brand's chart.
 * The sheet's Size + fit text for the product (sizeFit) is the one the page states (owner,
 * 3 Oct 2026: size information comes from her sheet, not from data stored on each product):
 *   - the fit line under the size options ("True to size", "Runs small…"), [data-wms-fit-note]
 *   - the calculator's own fit line and its one-size adjustment
 *   - the text under the size chart and in the "Size + fit" row, [data-wms-fit-text]
 * The section prints all of these from the product's own text (custom.product_size_and_fit) so
 * they are in the page from first paint; this script replaces them with the sheet's text when the
 * sheet lists the product (applySheetText), and marks what it wrote data-wms-fit-source="sheet".
 * A product the sheet does not list, or lists without text, keeps the product's own text, and
 * the calculator then reads the fit line the section printed (data-wms-fit-source="product").
 *
 * Only recommends sizes that exist as actual product variants.
 * Saves measurements to localStorage for cross-product recommendations.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'afterfive_my_measurements';

  /* ── helpers ── */
  function cmToIn(v) { return +(v / 2.54).toFixed(1); }
  function inToCm(v) { return +(v * 2.54).toFixed(1); }

  function parseRange(str) {
    if (!str || str === '-') return null;
    str = String(str).replace(/\s/g, '');
    if (str.charAt(0) === '<') {
      var maxVal = parseFloat(str.substring(1));
      return isNaN(maxVal) ? null : { min: 0, max: maxVal };
    }
    var parts = str.split('-');
    if (parts.length === 2) {
      var a = parseFloat(parts[0]), b = parseFloat(parts[1]);
      if (!isNaN(a) && !isNaN(b)) return { min: a, max: b };
    }
    var n = parseFloat(str);
    return isNaN(n) ? null : { min: n - 2, max: n + 2 };
  }

  /*
    A chart row written "<86" means "up to 86". parseRange() alone reads it as 0 to 86, and the
    engine scores the distance from the middle of a range (43), so on such a chart the row with
    the highest limit always won: waist 62 on S <68 / M <72 / L <76 answered L. Each "<N" cell
    therefore gets a real lower bound before scoring: the limit of the size below it in the
    brand's full chart, or, for the smallest size, N minus the step to the next size (4 cm when
    there is none). Cells written as a range or a single number are read as before.
  */
  var MEASURES = ['bust', 'waist', 'hips'];
  function isUpTo(str) {
    return String(str == null ? '' : str).replace(/\s/g, '').charAt(0) === '<';
  }
  function resolveChart(rows) {
    return rows.map(function (row, i) {
      var resolved = { label: row.label };
      MEASURES.forEach(function (key) {
        var range = parseRange(row[key]);
        if (range && isUpTo(row[key])) {
          var below = i > 0 ? parseRange(rows[i - 1][key]) : null;
          var above = i + 1 < rows.length ? parseRange(rows[i + 1][key]) : null;
          if (below && below.max < range.max) {
            range = { min: below.max, max: range.max };
          } else {
            var step = above && above.max > range.max ? above.max - range.max : 4;
            range = { min: range.max - step, max: range.max };
          }
        }
        resolved[key] = range;
      });
      return resolved;
    });
  }

  function getSaved() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || null; } catch (e) { return null; }
  }
  function saveMeasurements(data) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch (e) {}
  }

  /* ── data for one mount ── */
  function getProductInfo(mount) {
    if (!window.__AF_PRODUCTS) return null;
    var handle = (mount.getAttribute('data-wms-handle') || '').trim();
    return handle ? window.__AF_PRODUCTS[handle] || null : null;
  }

  function getChartData(mount) {
    if (!window.__AF_SIZE_CHARTS) return null;
    var productInfo = getProductInfo(mount);
    if (!productInfo || !productInfo.brand) return null;
    return window.__AF_SIZE_CHARTS[productInfo.brand.toUpperCase().trim()] || null;
  }

  function getAvailableSizes(mount) {
    var raw = mount.getAttribute('data-wms-available-sizes');
    if (!raw) return null;
    return raw.split(',').map(function (s) { return s.trim().toUpperCase(); });
  }

  function filterToAvailable(chartSizes, availableSizes) {
    if (!availableSizes || !availableSizes.length) return chartSizes;
    return chartSizes.filter(function (s) {
      return availableSizes.indexOf(s.label.trim().toUpperCase()) !== -1;
    });
  }

  /* ── recommendation engine ── */
  function recommend(sizes, bustCm, waistCm, hipsCm) {
    if (!sizes || !sizes.length) return null;
    var best = null, bestScore = Infinity;

    for (var i = 0; i < sizes.length; i++) {
      var s = sizes[i], score = 0, measurements = 0;
      // sizes are rows of resolveChart(): each measurement is already a { min, max } range or null
      var checks = [
        { val: bustCm, range: s.bust },
        { val: waistCm, range: s.waist },
        { val: hipsCm, range: s.hips }
      ];
      for (var c = 0; c < checks.length; c++) {
        if (checks[c].val && checks[c].range) {
          measurements++;
          var mid = (checks[c].range.min + checks[c].range.max) / 2;
          var diff = Math.abs(checks[c].val - mid);
          if (checks[c].val < checks[c].range.min) diff += (checks[c].range.min - checks[c].val) * 2;
          else if (checks[c].val > checks[c].range.max) diff += (checks[c].val - checks[c].range.max) * 2;
          score += diff;
        }
      }
      if (measurements > 0) {
        score = score / measurements;
        if (score < bestScore) { bestScore = score; best = { label: s.label, score: score, index: i }; }
      }
    }
    return best;
  }

  /* ── parse fit summary from Size+Fit text ── */
  function parseFitSummary(text) {
    if (!text) return '';
    // "No need to size up" is not advice to size up: take the phrase out before looking for the
    // words (Acy Dress and Cyan Top were labelled "Runs small" and bumped a size because of it).
    // sections/main-product.liquid applies the same rule to print the line under the size options.
    var t = text.toLowerCase().replace(/no need to size (up|down)/g, '');

    if (t.indexOf('true to size') !== -1) {
      if (t.indexOf('small fit') !== -1 || t.indexOf('runs small') !== -1) {
        return 'Runs small - we recommend sizing up';
      }
      return 'True to size';
    }

    if (t.indexOf('must size up') !== -1 || t.indexOf('runs small') !== -1 || t.indexOf('size up') !== -1 || t.indexOf('recommend sizing up') !== -1 || t.indexOf('small fit') !== -1) return 'Runs small - we recommend sizing up';
    if (t.indexOf('runs large') !== -1 || t.indexOf('size down') !== -1 || t.indexOf('recommend sizing down') !== -1) return 'Runs large - we recommend sizing down';
    return '';
  }

  /* ── the sheet's Size + fit text on the page ── */
  function fillText(el, text) {
    el.textContent = '';
    String(text).split('\n').forEach(function (line, i) {
      if (i) el.appendChild(document.createElement('br'));
      el.appendChild(document.createTextNode(line));
    });
  }

  function sheetText(handle) {
    var info = window.__AF_PRODUCTS && window.__AF_PRODUCTS[(handle || '').trim()];
    return info && info.sizeFit ? String(info.sizeFit).trim() : '';
  }

  function applySheetText() {
    document.querySelectorAll('[data-wms-fit-note][data-wms-handle], [data-wms-fit-text][data-wms-handle]').forEach(function (el) {
      var text = sheetText(el.getAttribute('data-wms-handle'));
      if (!text) return; // not in the sheet: the product's own text, printed by the section, stays
      if (el.hasAttribute('data-wms-fit-note')) {
        var summary = parseFitSummary(text);
        if (el.textContent.trim() !== summary) el.textContent = summary;
        el.hidden = !summary;
      } else {
        // the row prints the text inside the metafield's own span; the chart's note is the element itself
        fillText(el.querySelector('.metafield-multi_line_text_field') || el.querySelector('p') || el, text);
      }
      el.setAttribute('data-wms-fit-source', 'sheet');
    });
  }

  /* ── apply fit adjustment — bumps result up/down one size if needed ── */
  function applyFitAdjustment(result, sizes, summary) {
    if (!result || !sizes || !sizes.length) return result;
    var direction = 0;
    if (summary === 'Runs small - we recommend sizing up') direction = 1;
    else if (summary === 'Runs large - we recommend sizing down') direction = -1;
    if (direction === 0) return result;

    var newIndex = result.index + direction;
    if (newIndex < 0 || newIndex >= sizes.length) return result;
    return { label: sizes[newIndex].label, score: result.score, index: newIndex, adjusted: true };
  }

  /* ── select size on page ── */
  function selectSizeOnPage(sizeLabel) {
    var upper = sizeLabel.trim().toUpperCase();
    var scope = document.querySelector('product-info') || document;
    var radios = scope.querySelectorAll('.product-form__input input[type="radio"]');
    for (var r = 0; r < radios.length; r++) {
      if (radios[r].value.trim().toUpperCase() === upper) { radios[r].click(); return; }
    }
    var selects = scope.querySelectorAll('.product-form__input select');
    for (var s = 0; s < selects.length; s++) {
      for (var o = 0; o < selects[s].options.length; o++) {
        if (selects[s].options[o].value.trim().toUpperCase() === upper) {
          selects[s].value = selects[s].options[o].value;
          selects[s].dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
      }
    }
  }

  /* ── the recommendation line under the size options ──
     One line, never both (owner, 5 Oct 2026): where a recommendation shows, the product's fit
     note beside it ("True to size", "Runs small…") steps aside; where there is none, the fit
     note is the line. The fit note is the element just before the recommendation. */
  function fitNoteOf(rec) {
    var note = rec.previousElementSibling;
    return note && note.hasAttribute('data-wms-fit-note') ? note : null;
  }

  function showRecommendation(sizeLabel) {
    document.querySelectorAll('[data-wms-rec]').forEach(function (rec) {
      rec.textContent = '';
      rec.appendChild(document.createTextNode('Recommended for your measurements: '));
      var strong = document.createElement('strong');
      strong.textContent = sizeLabel;
      rec.appendChild(strong);
      rec.hidden = false;
      rec.classList.remove('is-reserved');
      var note = fitNoteOf(rec);
      if (note) note.classList.add('is-replaced');
    });
  }

  /* The section keeps the line's place for a returning shopper (class is-reserved, set before
     first paint) and holds the fit note back; when there is no recommendation to show, the
     place is given back and the fit note returns. */
  function releaseRecommendation() {
    document.querySelectorAll('[data-wms-rec]').forEach(function (rec) {
      if (!rec.hidden) return;
      rec.classList.remove('is-reserved');
      var note = fitNoteOf(rec);
      if (note) note.classList.remove('is-replaced');
    });
  }

  /* ── build the calculator into its mount ── */
  function initMount(mount) {
    if (mount.dataset.wmsReady) return;
    var rawSizes = getChartData(mount);
    if (!rawSizes) return;
    // lower bounds for "<N" cells come from the brand's full chart, before sizes this product
    // does not have are left out
    var sizes = filterToAvailable(resolveChart(rawSizes), getAvailableSizes(mount));
    if (!sizes.length) return;
    mount.dataset.wmsReady = 'true';

    var productInfo = getProductInfo(mount);
    var sizeFitText = productInfo ? String(productInfo.sizeFit || '').trim() : '';
    var fitSummary = parseFitSummary(sizeFitText);
    // The sheet's text decides the fit line and the size adjustment. Only when the sheet has no
    // text for the product does the line the section printed from the product's own text stand,
    // and the dialog and the adjustment then say the same as the page.
    var pageNote = document.querySelector('[data-wms-fit-note][data-wms-fit-source="product"]');
    var fromProduct = !sizeFitText && Boolean(pageNote);
    if (fromProduct) fitSummary = pageNote.hidden ? '' : pageNote.textContent.trim();
    var titleId = mount.getAttribute('data-wms-title-id') || 'wms-title';
    var currentUnit = 'cm';
    var PLACEHOLDERS = {
      cm: { bust: 'e.g. 86', waist: 'e.g. 68', hips: 'e.g. 92' },
      in: { bust: 'e.g. 34', waist: 'e.g. 27', hips: 'e.g. 36' }
    };

    mount.innerHTML = [
      '<h3 class="af-h3 af-wms__title" id="' + titleId + '">What’s my size?</h3>',
      '<p class="af-wms__intro af-body-sm">Enter at least one measurement and we’ll suggest a size for this style.</p>',
      '<p class="af-wms__fit af-body-sm" data-wms-fit hidden></p>',
      '<div data-wms-form>',
      '  <div class="af-toggle af-wms__units" role="group" aria-label="Units" data-wms-units>',
      '    <button type="button" class="af-option is-selected" data-unit="cm" aria-pressed="true">CM</button>',
      '    <button type="button" class="af-option" data-unit="in" aria-pressed="false">Inches</button>',
      '  </div>',
      '  <div class="af-wms__fields">',
      '    <div class="af-wms__field">',
      '      <label class="af-label" for="wms-bust">Bust (<span data-wms-unit-label>cm</span>)</label>',
      '      <input class="af-input" type="number" id="wms-bust" placeholder="e.g. 86" step="0.5" min="50" max="200" inputmode="decimal">',
      '    </div>',
      '    <div class="af-wms__field">',
      '      <label class="af-label" for="wms-waist">Waist (<span data-wms-unit-label>cm</span>)</label>',
      '      <input class="af-input" type="number" id="wms-waist" placeholder="e.g. 68" step="0.5" min="40" max="180" inputmode="decimal">',
      '    </div>',
      '    <div class="af-wms__field">',
      '      <label class="af-label" for="wms-hips">Hips (<span data-wms-unit-label>cm</span>)</label>',
      '      <input class="af-input" type="number" id="wms-hips" placeholder="e.g. 92" step="0.5" min="50" max="200" inputmode="decimal">',
      '    </div>',
      '  </div>',
      '  <p class="af-field-message af-field-message--error" id="wms-error" role="alert" data-wms-error hidden></p>',
      '  <button type="button" class="af-button af-button--full" data-wms-submit>Find my size</button>',
      '</div>',
      '<div class="af-wms__result" data-wms-result hidden aria-live="polite">',
      '  <p class="af-wms__answer af-body">Your size is <strong class="af-h2" data-wms-size></strong></p>',
      '  <div class="af-wms__actions">',
      '    <button type="button" class="af-button" data-wms-select>Select this size</button>',
      '    <button type="button" class="af-button af-button--secondary" data-wms-reset>Update measurements</button>',
      '  </div>',
      '</div>'
    ].join('\n');

    var q = function (sel) { return mount.querySelector(sel); };
    var form = q('[data-wms-form]');
    var resultBox = q('[data-wms-result]');
    var errorBox = q('[data-wms-error]');
    var inputs = { bust: q('#wms-bust'), waist: q('#wms-waist'), hips: q('#wms-hips') };

    if (fitSummary) {
      var fit = q('[data-wms-fit]');
      fit.textContent = fitSummary;
      fit.hidden = false;
      // The same line under the size options on the page, so "runs small" is said before the
      // dialog is opened (applySheetText has already written it when the sheet has the text).
      if (!fromProduct) {
        document.querySelectorAll('[data-wms-fit-note]').forEach(function (note) {
          if (note.textContent.trim() !== fitSummary) note.textContent = fitSummary;
          note.hidden = false;
        });
      }
    }
    mount.hidden = false;

    function setUnit(unit, convert) {
      if (unit === currentUnit) return;
      Object.keys(inputs).forEach(function (key) {
        var input = inputs[key];
        if (convert && input.value) {
          var val = parseFloat(input.value);
          if (!isNaN(val)) input.value = (unit === 'in' ? cmToIn(val) : inToCm(val)).toFixed(1);
        }
        input.placeholder = PLACEHOLDERS[unit][key];
      });
      mount.querySelectorAll('[data-wms-units] button').forEach(function (button) {
        var active = button.dataset.unit === unit;
        button.classList.toggle('is-selected', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      mount.querySelectorAll('[data-wms-unit-label]').forEach(function (el) { el.textContent = unit; });
      currentUnit = unit;
    }

    // The message is tied to the three fields while it shows (aria-describedby, aria-invalid),
    // so a screen reader repeats it on each field and the fields get the system's error border.
    function showError(message) {
      errorBox.textContent = message;
      errorBox.hidden = !message;
      Object.keys(inputs).forEach(function (key) {
        var input = inputs[key];
        if (message) {
          input.setAttribute('aria-describedby', errorBox.id);
          input.setAttribute('aria-invalid', 'true');
        } else {
          input.removeAttribute('aria-describedby');
          input.removeAttribute('aria-invalid');
        }
      });
    }

    function showResult(result) {
      form.hidden = true;
      q('[data-wms-size]').textContent = result.label;
      resultBox.hidden = false;
    }

    function showForm() {
      resultBox.hidden = true;
      form.hidden = false;
    }

    // A value outside the range its own field declares (min / max, in cm) is a typing slip, not a
    // measurement: a waist of 5 cm used to return "Your size is S".
    function outOfRange(key, cm) {
      if (!cm) return false;
      var min = parseFloat(inputs[key].getAttribute('min'));
      var max = parseFloat(inputs[key].getAttribute('max'));
      return (!isNaN(min) && cm < min) || (!isNaN(max) && cm > max);
    }

    function recommendFor(bustCm, waistCm, hipsCm) {
      var result = recommend(sizes, bustCm || 0, waistCm || 0, hipsCm || 0);
      return result ? applyFitAdjustment(result, sizes, fitSummary) : null;
    }

    Object.keys(inputs).forEach(function (key) {
      inputs[key].addEventListener('input', function () {
        if (!errorBox.hidden) showError('');
      });
    });

    q('[data-wms-units]').addEventListener('click', function (event) {
      var button = event.target.closest('button');
      if (button) setUnit(button.dataset.unit, true);
    });

    q('[data-wms-submit]').addEventListener('click', function () {
      var toCm = function (input) {
        var val = parseFloat(input.value);
        if (isNaN(val)) return 0;
        return currentUnit === 'in' ? inToCm(val) : val;
      };
      var bustCm = toCm(inputs.bust), waistCm = toCm(inputs.waist), hipsCm = toCm(inputs.hips);

      if (!bustCm && !waistCm && !hipsCm) {
        showError('Enter at least one measurement (bust, waist or hips).');
        inputs.bust.focus();
        return;
      }
      var entered = { bust: bustCm, waist: waistCm, hips: hipsCm };
      var wrong = Object.keys(inputs).filter(function (key) { return outOfRange(key, entered[key]); });
      var result = wrong.length ? null : recommendFor(bustCm, waistCm, hipsCm);
      if (!result) {
        showError('We couldn’t work out a size from those measurements. Please check them and try again.');
        if (wrong.length) inputs[wrong[0]].focus();
        return;
      }
      showError('');
      saveMeasurements({
        bust: bustCm || null,
        waist: waistCm || null,
        hips: hipsCm || null,
        preferredUnit: currentUnit,
        savedAt: new Date().toISOString()
      });
      showResult(result);
      showRecommendation(result.label);
      q('[data-wms-select]').focus();
    });

    q('[data-wms-reset]').addEventListener('click', function () {
      showForm();
      inputs.bust.focus();
    });

    q('[data-wms-select]').addEventListener('click', function () {
      selectSizeOnPage(q('[data-wms-size]').textContent);
      var dialog = mount.closest('modal-dialog');
      if (dialog && typeof dialog.hide === 'function') dialog.hide();
    });

    // Returning shopper: prefill the saved measurements and show the recommendation straight away.
    var saved = getSaved();
    if (saved && (saved.bust || saved.waist || saved.hips)) {
      setUnit(saved.preferredUnit === 'in' ? 'in' : 'cm', false);
      Object.keys(inputs).forEach(function (key) {
        if (saved[key]) inputs[key].value = currentUnit === 'in' ? cmToIn(saved[key]).toFixed(1) : saved[key];
      });
      var savedWrong = outOfRange('bust', saved.bust) || outOfRange('waist', saved.waist) || outOfRange('hips', saved.hips);
      var savedResult = savedWrong ? null : recommendFor(saved.bust, saved.waist, saved.hips);
      if (savedResult) {
        showResult(savedResult);
        showRecommendation(savedResult.label);
      }
    }
  }

  function init() {
    applySheetText();
    document.querySelectorAll('[data-wms-mount]').forEach(initMount);
    releaseRecommendation();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
